import { Fragment, useEffect, useState } from 'react'
import {
  Alert, Badge, Button, Code, Container, Group, Loader, Modal, MultiSelect,
  SegmentedControl, Select, Stack, Table, Tabs, Text, Title, Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconShieldLock, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react'
import type {
  RbacMatrixDto, RbacAuditEntryDto, StaffLevel, UserDto, UserPermissionsDto,
} from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import { fetchUsers } from '../shared/api/users'
import { updateUser } from '../shared/api/users'
import { fetchDepartments, type DepartmentRow } from '../shared/api/departments'
import {
  applyLevelPreset, fetchRbacAudit, fetchRbacMatrix, fetchUserPermissions, setUserOverride,
} from '../shared/api/rbac'

const ROLE_ORDER = ['admin', 'director', 'head', 'account', 'employee', 'observer'] as const
const LEVELS: StaffLevel[] = ['junior', 'middle', 'senior', 'lead']

export function AdminRbacPage() {
  const { user } = useAuthStore()
  if (!user) return null
  if (!user.permissions.includes('rbac.manage')) {
    return (
      <AppLayout>
        <Container><Text c="red" mt="md">Доступ только для права rbac.manage.</Text></Container>
      </AppLayout>
    )
  }
  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group gap="xs" mb="md">
          <IconShieldLock size={24} />
          <Title order={2}>Роли и доступы</Title>
        </Group>
        <Tabs defaultValue="matrix">
          <Tabs.List mb="md">
            <Tabs.Tab value="matrix">Роли и матрица</Tabs.Tab>
            <Tabs.Tab value="users">Доступы по пользователям</Tabs.Tab>
            <Tabs.Tab value="audit">Аудит</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="matrix"><MatrixTab /></Tabs.Panel>
          <Tabs.Panel value="users"><UserEditorTab selfId={user.id} /></Tabs.Panel>
          <Tabs.Panel value="audit"><AuditTab /></Tabs.Panel>
        </Tabs>
      </Container>
    </AppLayout>
  )
}

// ─── Вкладка 1: матрица ролей (read-only) ─────────────────────────────────────
function MatrixTab() {
  const [matrix, setMatrix] = useState<RbacMatrixDto | null>(null)
  useEffect(() => {
    fetchRbacMatrix().then(setMatrix).catch(() =>
      notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось загрузить матрицу' }))
  }, [])
  if (!matrix) return <Loader />
  const roleByKey = (role: string) => matrix.roles.find((r) => r.role === role)
  return (
    <Stack>
      <Group>
        {ROLE_ORDER.map((role) => {
          const r = roleByKey(role)
          return (
            <Badge key={role} size="lg" variant="light" radius="sm">
              {role} · {r?.user_count ?? 0} чел.
            </Badge>
          )
        })}
      </Group>
      <Text size="sm" c="dimmed">Матрица ролей — read-only (роли фиксированы в коде). Персонализация — на вкладке «Доступы по пользователям».</Text>
      <Table striped withTableBorder stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Право</Table.Th>
            {ROLE_ORDER.map((role) => <Table.Th key={role} ta="center">{role}</Table.Th>)}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {matrix.sections.map((section) => (
            <Fragment key={section.title}>
              <Table.Tr bg="var(--mantine-color-gray-1)">
                <Table.Td colSpan={ROLE_ORDER.length + 1}><Text fw={600} size="sm">{section.title}</Text></Table.Td>
              </Table.Tr>
              {section.permissions.map((p) => (
                <Table.Tr key={p.key}>
                  <Table.Td>
                    <Tooltip label={p.desc} multiline w={260} withArrow>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm">{p.label}</Text>
                        <Code fz={10}>{p.key}</Code>
                      </Group>
                    </Tooltip>
                  </Table.Td>
                  {ROLE_ORDER.map((role) => (
                    <Table.Td key={role} ta="center">
                      {roleByKey(role)?.permission_keys.includes(p.key) ? '✓' : '—'}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

// ─── Вкладка 2: доступы по пользователям ──────────────────────────────────────
function UserEditorTab({ selfId }: { selfId: string }) {
  const [users, setUsers] = useState<UserDto[]>([])
  const [departments, setDepartments] = useState<DepartmentRow[]>([])
  const [matrix, setMatrix] = useState<RbacMatrixDto | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [perms, setPerms] = useState<UserPermissionsDto | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [presetLevel, setPresetLevel] = useState<StaffLevel | null>(null)
  const [confirmPreset, setConfirmPreset] = useState(false)

  useEffect(() => {
    fetchUsers({ active: true }).then(setUsers).catch(() => undefined)
    fetchDepartments().then(setDepartments).catch(() => undefined)
    fetchRbacMatrix().then(setMatrix).catch(() => undefined)
  }, [])

  async function loadPerms(id: string) {
    setSelectedId(id)
    setPerms(null)
    try { setPerms(await fetchUserPermissions(id)) }
    catch { notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось загрузить права' }) }
  }

  const isAdmin = perms?.user.role === 'admin'
  const isSelf = perms?.user.is_self ?? false
  const locked = isAdmin || isSelf

  function stateOf(key: string): 'default' | 'grant' | 'deny' {
    const o = perms?.overrides.find((x) => x.permission_key === key)
    return o ? o.effect : 'default'
  }

  async function changeOverride(key: string, effect: 'default' | 'grant' | 'deny') {
    if (!selectedId) return
    setBusyKey(key)
    try {
      setPerms(await setUserOverride(selectedId, key, effect))
      notifications.show({ color: 'green', title: 'Сохранено', message: `${key}: ${effect}. Вступит в силу при следующем запросе сотрудника.` })
    } catch {
      notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось изменить право (бэкенд отклонил)' })
    } finally { setBusyKey(null) }
  }

  async function changeDepartments(ids: string[]) {
    if (!selectedId) return
    try {
      await updateUser(selectedId, { department_ids: ids })
      setPerms(await fetchUserPermissions(selectedId))
      notifications.show({ color: 'green', title: 'Сохранено', message: 'Отделы обновлены' })
    } catch { notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось обновить отделы' }) }
  }

  async function doApplyPreset() {
    if (!selectedId || !presetLevel) return
    setConfirmPreset(false)
    try {
      await applyLevelPreset(selectedId, presetLevel)
      setPerms(await fetchUserPermissions(selectedId))
      notifications.show({ color: 'green', title: 'Уровень применён', message: `Override заменены набором «${presetLevel}».` })
    } catch { notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось применить уровень' }) }
  }

  return (
    <Stack>
      <Select
        label="Сотрудник"
        placeholder="Выберите сотрудника"
        searchable
        data={users.map((u) => ({ value: u.id, label: `${u.full_name} · ${u.role}` }))}
        value={selectedId}
        onChange={(v) => v && loadPerms(v)}
        maw={420}
      />

      {selectedId && !perms && <Loader />}

      {perms && (
        <>
          {locked && (
            <Alert icon={<IconInfoCircle size={16} />} color={isAdmin ? 'gray' : 'yellow'}>
              {isAdmin
                ? 'Это admin — у него всегда все права, строка только для чтения.'
                : 'Это вы сами — нельзя запереть себя; редактирование собственных прав отключено.'}
            </Alert>
          )}

          <Group align="flex-end">
            <MultiSelect
              label="Отделы"
              data={departments.map((d) => ({ value: d.id, label: d.name }))}
              value={perms.department_ids}
              onChange={changeDepartments}
              disabled={isSelf}
              maw={420}
            />
            <Select
              label="Применить уровень"
              placeholder="junior / middle / senior / lead"
              data={LEVELS.map((l) => ({ value: l, label: l }))}
              value={presetLevel}
              onChange={(v) => setPresetLevel(v as StaffLevel)}
              disabled={locked}
              w={220}
            />
            <Button disabled={locked || !presetLevel} onClick={() => setConfirmPreset(true)}>Применить</Button>
          </Group>

          <Text size="sm" c="dimmed">
            Три состояния по каждому праву: <b>из роли</b> (дефолт), <b>разрешить</b> (grant), <b>запретить</b> (deny).
            Изменения вступают в силу при следующем запросе сотрудника.
          </Text>

          <Table withTableBorder>
            <Table.Tbody>
              {matrix?.sections.map((section) => (
                <Fragment key={section.title}>
                  <Table.Tr bg="var(--mantine-color-gray-1)">
                    <Table.Td colSpan={2}><Text fw={600} size="sm">{section.title}</Text></Table.Td>
                  </Table.Tr>
                  {section.permissions.map((p) => {
                    const inRole = perms.role_defaults.includes(p.key)
                    return (
                      <Table.Tr key={p.key}>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm">{p.label}</Text>
                            <Code fz={10}>{p.key}</Code>
                            <Text size="xs" c="dimmed">{inRole ? '(из роли: есть)' : '(из роли: нет)'}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td w={300}>
                          <SegmentedControl
                            size="xs"
                            disabled={locked || busyKey === p.key}
                            value={stateOf(p.key)}
                            onChange={(v) => changeOverride(p.key, v as 'default' | 'grant' | 'deny')}
                            data={[
                              { label: 'Из роли', value: 'default' },
                              { label: 'Разрешить', value: 'grant' },
                              { label: 'Запретить', value: 'deny' },
                            ]}
                          />
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Fragment>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      <Modal opened={confirmPreset} onClose={() => setConfirmPreset(false)} title="Применить уровень?">
        <Stack>
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow">
            Это <b>заменит</b> текущие персональные override сотрудника набором уровня «{presetLevel}».
            Действие аудируется.
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmPreset(false)}>Отмена</Button>
            <Button color="orange" onClick={doApplyPreset}>Применить «{presetLevel}»</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

// ─── Вкладка 3: аудит прав ────────────────────────────────────────────────────
function AuditTab() {
  const [rows, setRows] = useState<RbacAuditEntryDto[] | null>(null)
  useEffect(() => {
    fetchRbacAudit(50).then(setRows).catch(() =>
      notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось загрузить аудит' }))
  }, [])
  if (!rows) return <Loader />
  const label = (t: string) => t === 'permissions_preset_applied' ? 'пресет уровня' : 'override права'
  const summary = (e: RbacAuditEntryDto) => {
    const to = e.to_value as { level?: string; key?: string; effect?: string | null } | null
    if (!to) return '—'
    if (to.level) return `уровень: ${to.level}`
    if (to.key) return `${to.key} → ${to.effect ?? 'из роли'}`
    return '—'
  }
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Когда</Table.Th>
          <Table.Th>Кто</Table.Th>
          <Table.Th>Кому</Table.Th>
          <Table.Th>Событие</Table.Th>
          <Table.Th>Что</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((e) => (
          <Table.Tr key={e.id}>
            <Table.Td><Text size="xs">{new Date(e.created_at).toLocaleString('ru-RU')}</Text></Table.Td>
            <Table.Td>{e.actor_name ?? '—'}</Table.Td>
            <Table.Td>{e.user_name}</Table.Td>
            <Table.Td><Badge variant="light" size="sm">{label(e.event_type)}</Badge></Table.Td>
            <Table.Td><Text size="sm">{summary(e)}</Text></Table.Td>
          </Table.Tr>
        ))}
        {rows.length === 0 && (
          <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center">Пока нет изменений прав.</Text></Table.Td></Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  )
}
